import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableDropdownBox } from '@/components/SearchableDropdownBox';
import { ArrowLeft, Save, Send, Loader2, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

/** When multiPart is true, in/out times are left empty for user to fill. When false, default 08:00/20:00. */
function getInitialPartLine(multiPart) {
  return {
    partId: '',
    batchNo: '',
    cycleTime: '',
    inTime: multiPart ? '' : '08:00',
    outTime: multiPart ? '' : '20:00',
    targetQty: '',
    producedQty: '',
    // Legacy single rejected qty + free-text reason; kept for backwards compatibility in payload,
    // but the UI now uses rejectionLines (qty + master reason) and derives totals from that.
    rejectedQty: '',
    rejectionReason: '',
    rejectionLines: [],
    reworkBacklog: 0,
    reworkedThisRun: '',
  };
}

function ProductionEntryPage({ hideBackButton = false, onSaved }) {
  const urlParams = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const runId = urlParams.id;
  const isEditing = Boolean(runId);
  const user = auth.user;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);
  const [derivatives, setDerivatives] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [downtimeReasons, setDowntimeReasons] = useState([]);
  const [status, setStatus] = useState('DRAFT');

  // Form fields
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [shift, setShift] = useState('DAY');
  const [machineId, setMachineId] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [derivativeId, setDerivativeId] = useState('NONE');
  const [setupStage, setSetupStage] = useState('I');
  const [downtimeLines, setDowntimeLines] = useState([{ hrs: '', mins: '', reasonId: '', reasonCode: '', reasonLabel: '' }]);
  const [remarks, setRemarks] = useState('');
  const [multiPartEnabled, setMultiPartEnabled] = useState(false);
  const [partLines, setPartLines] = useState([getInitialPartLine(false)]);

  const isReadOnly = status !== 'DRAFT' && user && user.role === 'SUPERVISOR';

  function resetFormToDefaults() {
    setDate(new Date().toISOString().split('T')[0]);
    setShift('DAY');
    setMachineId('');
    setOperatorId('');
    setDerivativeId('NONE');
    setSetupStage('I');
    setDowntimeLines([{ hrs: '', mins: '', reasonId: '', reasonCode: '', reasonLabel: '' }]);
    setRemarks('');
    setStatus('DRAFT');
    setMultiPartEnabled(false);
    setPartLines([getInitialPartLine(false)]);
  }

  function minutesToHrMin(totalMins) {
    return { hrs: Math.floor(totalMins / 60), mins: totalMins % 60 };
  }

  function toSingleLine(str) {
    return (str || '').replace(/\s+/g, ' ').trim();
  }

  function hrMinToMinutes(hrs, mins) {
    return (parseInt(hrs, 10) || 0) * 60 + (parseInt(mins, 10) || 0);
  }

  /**
   * Compute target quantity from shift duration and cycle time.
   * - useAbsoluteShiftHours (multi-part): use raw shift duration in hours as working time.
   * - single part: 12 hr shift → 10 hrs working, 8 hr shift → 7 hrs working; else duration.
   * - Realistic cycle time = machine cycle time (x) + 8 seconds (loading/unloading)
   * - Target = working seconds / realistic cycle time
   */
  function computeTargetFromShiftAndCycle(inT, outT, cycleSec, useAbsoluteShiftHours) {
    if (!inT || !outT || cycleSec === '' || cycleSec === undefined) return null;
    const c = parseInt(cycleSec, 10);
    if (isNaN(c) || c < 0) return null;
    const [inH, inM] = inT.trim().split(':').map(Number);
    const [outH, outM] = outT.trim().split(':').map(Number);
    if (isNaN(inH) || isNaN(outH)) return null;
    const inMinutes = (inH || 0) * 60 + (inM || 0);
    const outMinutes = (outH || 0) * 60 + (outM || 0);
    const durationMinutes = outMinutes >= inMinutes ? outMinutes - inMinutes : 24 * 60 - inMinutes + outMinutes;
    const durationHours = durationMinutes / 60;
    let workingHours;
    if (useAbsoluteShiftHours) {
      workingHours = Math.max(0, durationHours);
    } else {
      if (durationHours >= 10.5) workingHours = 10;        // 12 hr shift: 10 hrs work, 2 hrs misc
      else if (durationHours >= 7 && durationHours <= 8) workingHours = 7;   // 8 hr shift: 7 hrs work, 1 hr misc
      else workingHours = Math.max(0, durationHours);
    }
    const workingSeconds = workingHours * 3600;
    const realisticCycleTime = c + 8;
    if (realisticCycleTime <= 0) return null;
    return Math.round(workingSeconds / realisticCycleTime);
  }

  useEffect(function autoFillPartTargets() {
    if (isReadOnly) return;
    var useAbsolute = multiPartEnabled;
    setPartLines(function(prev) {
      return prev.map(function(line) {
        const computed = computeTargetFromShiftAndCycle(line.inTime, line.outTime, line.cycleTime, useAbsolute);
        if (computed != null) return { ...line, targetQty: String(computed) };
        return { ...line, targetQty: '' };
      });
    });
  }, [isReadOnly, multiPartEnabled, partLines.map(function(p) { return p.inTime + p.outTime + p.cycleTime; }).join('|')]);

  useEffect(function loadMaster() {
    api.get('/machines').then(function(r) { setMachines((r.data || []).filter((m) => m.active !== false)); });
    api.get('/parts').then(function(r) { setParts(r.data); });
    api.get('/operators').then(function(r) { setOperators(r.data); });
    api.get('/derivatives').then(function(r) { setDerivatives(r.data); });
    api.get('/rejection-reasons').then(function(r) { setRejectionReasons(r.data || []); });
    api.get('/downtime-reasons').then(function(r) { setDowntimeReasons(r.data || []); });
  }, []);

  function refreshReworkBacklogForLine(lineIndex, partId, batchNo, machineIdOverride) {
    const part = partId;
    const batch = (batchNo || '').trim();
    const mid = machineIdOverride != null ? machineIdOverride : machineId;
    if (!mid || !part || !batch) {
      setPartLines(function(prev) {
        const n = [...prev];
        if (!n[lineIndex]) return prev;
        n[lineIndex] = { ...n[lineIndex], reworkBacklog: 0, reworkedThisRun: '' };
        return n;
      });
      return;
    }
    api
      .get('/rework-backlog', {
        params: { machine_id: mid, part_id: part, batch_no: batch },
      })
      .then(function(res) {
        const qty = res.data && typeof res.data.qty === 'number' ? res.data.qty : parseInt(res.data?.qty, 10) || 0;
        setPartLines(function(prev) {
          const n = [...prev];
          if (!n[lineIndex]) return prev;
          n[lineIndex] = { ...n[lineIndex], reworkBacklog: qty };
          return n;
        });
      })
      .catch(function(err) {
        setPartLines(function(prev) {
          const n = [...prev];
          if (!n[lineIndex]) return prev;
          n[lineIndex] = { ...n[lineIndex], reworkBacklog: 0 };
          return n;
        });
      });
  }

  const reworkFetchKey = partLines.map(function(l) {
    return (l.partId || '') + '|' + (l.batchNo || '').trim();
  }).join(';');
  useEffect(function refreshReworkBacklogWhenKeysChange() {
    if (!machineId || isEditing) return;
    partLines.forEach(function(line, idx) {
      const batch = (line.batchNo || '').trim();
      if (line.partId && batch) {
        refreshReworkBacklogForLine(idx, line.partId, line.batchNo, machineId);
      }
    });
  }, [machineId, reworkFetchKey]);

  useEffect(function loadRun() {
    if (!isEditing) return;
    setLoading(true);
    api.get('/production-runs/' + runId).then(function(res) {
      var d = res.data;
      setDate(d.date);
      setShift(d.shift);
      setMachineId(d.machine_id);
      setOperatorId(d.operator_id);
      var derivCode = d.derivative || '';
      if (derivCode && derivatives.length > 0) {
        var found = derivatives.find(function(der) { return der.code === derivCode; });
        if (found) setDerivativeId(found.id);
      } else {
        setDerivativeId('NONE');
      }
      setSetupStage(d.setup_stage || 'I');
      var runIn = d.metrics?.in_time || '08:00';
      var runOut = d.metrics?.out_time || '20:00';
      var isMultiPartRun = d.parts && d.parts.length > 1;
      setMultiPartEnabled(isMultiPartRun);
      var dtBreakdown = d.metrics?.downtime_breakdown || [];
      var mappedDt = [];
      if (dtBreakdown.length > 0) {
        mappedDt = dtBreakdown.map(function(row) {
          var mins = parseInt(row.minutes, 10) || 0;
          var { hrs, mins: m } = minutesToHrMin(mins);
          var code = (row.reason_code || '').toUpperCase();
          var found = downtimeReasons.find(function(dr) { return (dr.code || '').toUpperCase() === code; });
          var label = toSingleLine(row.reason_label || (found ? (found.code || '') + (found.description ? ' - ' + found.description : '') : ''));
          return { hrs: (hrs === 0 && m === 0) ? '' : String(hrs), mins: (hrs === 0 && m === 0) ? '' : String(m), reasonId: found ? found.id : '', reasonCode: code, reasonLabel: label };
        });
      } else {
        var dt = minutesToHrMin(d.metrics.downtime_minutes || 0);
        var totalZero = (dt.hrs || 0) === 0 && (dt.mins || 0) === 0;
        mappedDt = [{ hrs: totalZero ? '' : String(dt.hrs ?? 0), mins: totalZero ? '' : String(dt.mins ?? 0), reasonId: '', reasonCode: '', reasonLabel: '' }];
      }
      if (mappedDt.length === 0) mappedDt = [{ hrs: '', mins: '', reasonId: '', reasonCode: '', reasonLabel: '' }];
      setDowntimeLines(mappedDt);
      setRemarks(d.metrics.remarks || '');
      setStatus(d.status);
      if (d.parts && d.parts.length > 0) {
        const mapped = d.parts.map(function(p) {
          const breakdown = p.rejection_breakdown || [];
          let rejectionLines = [];
          if (breakdown.length > 0) {
            rejectionLines = breakdown.map(function(row) {
              const code = (row.reason_code || '').toUpperCase();
              const label = row.reason_label || '';
              const found = rejectionReasons.find(function(rr) {
                return (rr.code || '').toUpperCase() === code;
              });
              return {
                qty: String(row.qty != null ? row.qty : 0),
                reasonId: found ? found.id : '',
                reasonLabel: label || (found ? (found.code || '') + (found.description ? ' - ' + found.description : '') : ''),
                reasonCode: code,
              };
            });
          } else {
            const initialRejected = p.rejected_qty != null ? String(p.rejected_qty) : '';
            const initialReason = p.rejection_reason || '';
            const hasRejection = (initialRejected && initialRejected !== '0') || initialReason;
            if (hasRejection) {
              rejectionLines = [{ qty: initialRejected, reasonId: '', reasonLabel: initialReason }];
            }
          }
          return {
            partId: p.part_id || '',
            batchNo: p.batch_no || '',
            cycleTime: p.cycle_time != null ? String(p.cycle_time) : '',
            inTime: isMultiPartRun ? (p.in_time || '') : ((p.in_time && p.in_time.trim()) ? p.in_time : runIn),
            outTime: isMultiPartRun ? (p.out_time || '') : ((p.out_time && p.out_time.trim()) ? p.out_time : runOut),
            targetQty: p.target_qty != null ? String(p.target_qty) : '',
            producedQty: p.produced_qty != null ? String(p.produced_qty) : '',
            rejectedQty: String(rejectionLines.reduce(function(s, r) { return s + (parseInt(r.qty, 10) || 0); }, 0)),
            rejectionReason: (p.rejection_reason || '').trim(),
            rejectionLines,
            reworkBacklog: 0,
            reworkedThisRun: p.rework_consumed != null ? String(p.rework_consumed) : '',
          };
        });
        setPartLines(mapped);
        // After initial load, refresh rework backlog for each part line (pass d.machine_id since state may not have updated yet)
        mapped.forEach(function(line, idx) {
          if (line.partId && line.batchNo) {
            refreshReworkBacklogForLine(idx, line.partId, line.batchNo, d.machine_id);
          }
        });
      } else {
        const initialRejected = d.metrics.rejected_qty != null ? String(d.metrics.rejected_qty) : '';
        const initialReason = d.metrics.rejection_reason || '';
        const hasRejection = (initialRejected && initialRejected !== '0') || initialReason;
        const singleLine = {
          partId: d.part_id || '',
          batchNo: d.metrics.batch_no || '',
          cycleTime: d.cycle_time != null ? String(d.cycle_time) : '',
          inTime: runIn,
          outTime: runOut,
          targetQty: d.metrics.target_qty != null ? String(d.metrics.target_qty) : '',
          producedQty: d.metrics.produced_qty != null ? String(d.metrics.produced_qty) : '',
          rejectedQty: initialRejected,
          rejectionReason: initialReason,
          rejectionLines: hasRejection
            ? [{ qty: initialRejected, reasonId: '', reasonLabel: initialReason }]
            : [],
          reworkBacklog: 0,
          reworkedThisRun: '',
        };
        setPartLines([singleLine]);
        if (singleLine.partId && singleLine.batchNo) {
          refreshReworkBacklogForLine(0, singleLine.partId, singleLine.batchNo, d.machine_id);
        }
      }
    }).finally(function() { setLoading(false); });
  }, [runId, isEditing, derivatives, rejectionReasons, downtimeReasons]);

  function handleSave(andSubmit) {
    if (!date?.trim()) {
      toast.error('Date is required');
      return;
    }
    if (!machineId || !operatorId) {
      toast.error('Select Machine and Operator');
      return;
    }
    if (!derivativeId || derivativeId === 'NONE') {
      toast.error('Derivative is required');
      return;
    }
    if (andSubmit) {
      var dtLines = downtimeLines || [];
      for (var di = 0; di < dtLines.length; di++) {
        var dtRow = dtLines[di];
        var hrsVal = dtRow?.hrs;
        var minsVal = dtRow?.mins;
        var dHrs = parseInt(hrsVal, 10) || 0;
        var dMins = parseInt(minsVal, 10) || 0;
        var dtTotal = dHrs * 60 + dMins;
        var hasReason = !!(dtRow?.reasonId || (dtRow?.reasonCode || '').trim());
        var hasHrs = hrsVal !== '' && hrsVal !== undefined && hrsVal !== null;
        var hasMins = minsVal !== '' && minsVal !== undefined && minsVal !== null;
        var isEmpty = !hasHrs && !hasMins && !hasReason;
        if (isEmpty) {
          toast.error('Fill or remove empty downtime rows');
          return;
        }
        if (dtTotal === 0 && !hasReason) {
          continue;
        }
        if (dtTotal > 0 && !hasReason) {
          toast.error('Downtime reason is required for every row with non-zero time');
          return;
        }
        if (hasReason && !hasHrs && !hasMins) {
          toast.error('Enter hrs and mins for every downtime row with a reason selected');
          return;
        }
      }
    }
    var totalTarget = 0, totalProduced = 0;
    for (var i = 0; i < partLines.length; i++) {
      var line = partLines[i];
      if (!line.partId) {
        toast.error('Select Part for every row');
        return;
      }
      if (!line.batchNo || !line.batchNo.trim()) {
        toast.error('Batch No is required for each part');
        return;
      }
      if (line.cycleTime === '') {
        toast.error('Cycle Time is required for each part');
        return;
      }
      if (!(line.inTime || '').trim() || !(line.outTime || '').trim()) {
        toast.error('In Time and Out Time are required for each part');
        return;
      }
      if (line.targetQty === '' || parseInt(line.targetQty, 10) < 0) {
        toast.error('Target Qty is required for each part');
        return;
      }
      if (andSubmit && line.producedQty === '') {
        toast.error('Produced Qty is required for each part');
        return;
      }
      // Rejection breakdown validation: sum rows, ensure reasons when qty > 0
      var lines = line.rejectionLines || [];
      var rejectedSum = 0;
      for (var ri = 0; ri < lines.length; ri++) {
        var row = lines[ri];
        var rq = row && row.qty !== undefined && row.qty !== null && row.qty !== ''
          ? parseInt(row.qty, 10) || 0
          : 0;
        if (rq < 0) {
          toast.error('Rejected Qty cannot be negative');
          return;
        }
        var hasRejReason = !!(row.reasonId || (row.reasonLabel || '').trim());
        if (rq > 0 && !hasRejReason) {
          toast.error('Rejection reason is required for every rejected quantity');
          return;
        }
        if (hasRejReason && rq === 0) {
          toast.error('Enter rejected qty for every row with a reason selected');
          return;
        }
        rejectedSum += rq;
      }
      var t = parseInt(line.targetQty, 10) || 0;
      var p = parseInt(line.producedQty, 10) || 0;
      var r = rejectedSum || 0;
      totalTarget += t;
      totalProduced += p;
    }

    setSaving(true);
    var dtBreakdownToSave = (downtimeLines || [])
      .filter(function(row) {
        var m = hrMinToMinutes(row.hrs, row.mins);
        return m > 0;
      })
      .map(function(row) {
        return {
          minutes: hrMinToMinutes(row.hrs, row.mins),
          reason_code: (row.reasonCode || '').toUpperCase(),
          reason_label: row.reasonLabel || '',
        };
      });
    var downtimeTotalMins = dtBreakdownToSave.reduce(function(s, r) { return s + (r.minutes || 0); }, 0);
    var derivativeCode = '';
    if (derivativeId && derivativeId !== 'NONE') {
      var selectedDeriv = derivatives.find(function(d) { return d.id === derivativeId; });
      if (selectedDeriv) derivativeCode = selectedDeriv.code;
    }
    var linesToSave = multiPartEnabled ? partLines : partLines.slice(0, 1);
    var partsPayload = linesToSave.map(function(line) {
      var breakdown = (line.rejectionLines || []).filter(function(row) {
        return row && row.qty !== undefined && row.qty !== null && row.qty !== '' && (parseInt(row.qty, 10) || 0) > 0;
      });
      var rejectedTotal = breakdown.reduce(function(sum, row) {
        return sum + (parseInt(row.qty, 10) || 0);
      }, 0);
      var rejectionReasonSummary = breakdown.map(function(row) {
        var label = row.reasonLabel || '';
        return (row.qty ? String(row.qty) : '0') + (label ? ' - ' + label : '');
      }).join('; ');
      return {
        part_id: line.partId,
        batch_no: (line.batchNo || '').trim(),
        cycle_time: parseInt(line.cycleTime, 10) || 0,
        in_time: (line.inTime || '').trim() || '08:00',
        out_time: (line.outTime || '').trim() || '20:00',
        target_qty: parseInt(line.targetQty, 10) || 0,
        produced_qty: parseInt(line.producedQty, 10) || 0,
        rejected_qty: rejectedTotal,
        rejection_reason: rejectionReasonSummary || (line.rejectionReason || '').trim(),
        rejection_breakdown: breakdown.map(function(row) {
          return {
            qty: parseInt(row.qty, 10) || 0,
            reason_code: (row.reasonCode || '').toUpperCase(),
            reason_label: row.reasonLabel || '',
          };
        }),
        rework_consumed: parseInt(line.reworkedThisRun, 10) || 0,
      };
    });
    var firstPart = partsPayload[0];
    var payload = {
      date: date,
      shift: shift,
      machine_id: machineId,
      part_id: partsPayload[0]?.part_id || '',
      operator_id: operatorId,
      derivative: derivativeCode,
      setup_stage: setupStage,
      cycle_time: partsPayload[0]?.cycle_time || 0,
      metrics: {
        in_time: firstPart?.in_time || '08:00',
        out_time: firstPart?.out_time || '20:00',
        setup_time: 0,
        target_qty: 0,
        produced_qty: 0,
        rejected_qty: 0,
        rejection_reason: '',
        downtime_minutes: downtimeTotalMins,
        downtime_breakdown: dtBreakdownToSave,
        batch_no: partsPayload[0]?.batch_no || '',
        remarks: remarks
      },
      parts: partsPayload,
      tool_usage: [],
      inspections: []
    };

    var promise = isEditing ? api.put('/production-runs/' + runId, payload) : api.post('/production-runs', payload);
    promise
      .then(function (res) {
        var newId = isEditing ? runId : res.data.id;
        toast.success(isEditing ? 'Updated successfully' : 'Created successfully');
        if (andSubmit) {
          return api.post('/production-runs/' + newId + '/submit').then(function () {
            toast.success('Submitted for approval');
            window.dispatchEvent(new CustomEvent('approvals-changed'));
          });
        }
      })
      .then(function () {
        if (!isEditing && typeof onSaved === 'function') {
          // For create (draft or submit) in embedded entry, reset form and let parent refresh.
          resetFormToDefaults();
          onSaved();
        } else {
          navigate('/production-runs');
        }
      })
      .catch(function (err) {
        toast.error('Save failed: ' + (err.response?.data?.detail || 'Unknown error'));
      })
      .finally(function () {
        setSaving(false);
      });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Common input style for consistent height
  const inputClass = "h-10";
  const selectTriggerClass = "h-10";

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="production-entry-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {!hideBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
              {isEditing ? 'Edit Production Run' : 'New Production Run'}
            </h1>
            {isEditing && (
              <p className="text-sm text-slate-500">
                Status: <span className="font-medium px-2 py-0.5 rounded bg-slate-100">{status}</span>
              </p>
            )}
          </div>
        </div>
        {!isReadOnly && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button size="sm" onClick={() => handleSave(true)} disabled={saving} data-testid="save-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Submit
            </Button>
          </div>
        )}
      </div>

      {/* Read-only warning */}
      {isReadOnly && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
          <p className="text-yellow-800 text-sm">This run has been submitted and cannot be edited.</p>
        </div>
      )}

      {/* Basic Information Card */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Basic Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Date *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={isReadOnly} className={`${inputClass} w-40`} data-testid="input-date" />
              </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Shift *</Label>
              <Select value={shift} onValueChange={setShift} disabled={isReadOnly}>
                <SelectTrigger className={selectTriggerClass} data-testid="input-shift"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DAY">Day</SelectItem>
                  <SelectItem value="NIGHT">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Machine *</Label>
              <Select value={machineId} onValueChange={setMachineId} disabled={isReadOnly}>
                <SelectTrigger className={selectTriggerClass} data-testid="input-machine"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Setup Stage *</Label>
              <Select value={setupStage} onValueChange={setSetupStage} disabled={isReadOnly}>
                <SelectTrigger className={selectTriggerClass} data-testid="input-setup-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="I">I (First)</SelectItem>
                  <SelectItem value="II">II (Second)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Derivative *</Label>
              <Select value={derivativeId} onValueChange={setDerivativeId} disabled={isReadOnly}>
                <SelectTrigger className={selectTriggerClass} data-testid="input-derivative"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">— None —</SelectItem>
                  {derivatives.map((d) => <SelectItem key={d.id} value={d.id}>{d.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Operator *</Label>
              <Select value={operatorId} onValueChange={setOperatorId} disabled={isReadOnly}>
                <SelectTrigger className={selectTriggerClass} data-testid="input-operator"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <Label className="text-xs font-medium">Multi part</Label>
              <Switch
                checked={multiPartEnabled}
                onCheckedChange={(checked) => {
                  setMultiPartEnabled(checked);
                  if (checked) {
                    setPartLines(function(prev) {
                      const first = prev[0] ? { ...prev[0], inTime: '', outTime: '' } : getInitialPartLine(true);
                      return [first];
                    });
                  } else {
                    setPartLines(function(prev) {
                      const first = prev[0] ? { ...prev[0], inTime: prev[0].inTime || '08:00', outTime: prev[0].outTime || '20:00' } : getInitialPartLine(false);
                      return [first];
                    });
                  }
                }}
                disabled={isReadOnly}
                data-testid="toggle-multi-part"
              />
            </div>
            {/* Batch No and Cycle Time are now per-part, see Parts Produced section */}
          </div>
        </CardContent>
      </Card>

      {/* Parts produced (single or multiple parts per run) */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Parts Produced</CardTitle>
          {!isReadOnly && multiPartEnabled && (
            <Button type="button" variant="outline" size="sm" onClick={() => setPartLines(function(prev) { return [...prev, getInitialPartLine(true)]; })} data-testid="add-part-btn">
              <Plus className="w-4 h-4 mr-1" /> Add part
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(multiPartEnabled ? partLines : partLines.slice(0, 1)).map(function(line, index) {
              return (
                <div key={index} className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">Part {index + 1}</span>
                    {!isReadOnly && multiPartEnabled && partLines.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setPartLines(function(prev) { return prev.filter(function(_, i) { return i !== index; }); })} data-testid="remove-part-btn">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  {/* Row 1: Part / Batch / In / Out */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Part No *</Label>
                      <div className="w-40">
                        <SearchableDropdownBox
                          options={parts.map((p) => ({ value: p.id, label: p.part_no }))}
                          value={line.partId}
                          onChange={(v) => {
                            setPartLines(function(prev) {
                              const n = [...prev];
                              n[index] = { ...n[index], partId: v };
                              return n;
                            });
                            refreshReworkBacklogForLine(index, v, line.batchNo, machineId);
                          }}
                          placeholder="Select"
                          disabled={isReadOnly}
                          searchPlaceholder="Type to search part no..."
                          emptyMessage="No part found."
                          triggerClassName="w-full"
                          triggerTestId={index === 0 ? 'input-part' : `input-part-${index}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Batch No *</Label>
                      <div className="w-40">
                        <Input
                          value={line.batchNo}
                          onChange={(e) =>
                            setPartLines(function(prev) {
                              const n = [...prev];
                              const newBatch = e.target.value;
                              n[index] = { ...n[index], batchNo: newBatch };
                              refreshReworkBacklogForLine(index, n[index].partId, newBatch, machineId);
                              return n;
                            })
                          }
                          onBlur={() => {
                            const batch = (line.batchNo || '').trim();
                            if (line.partId && batch && machineId) {
                              refreshReworkBacklogForLine(index, line.partId, line.batchNo, machineId);
                            }
                          }}
                          disabled={isReadOnly}
                          className={`${inputClass} w-full`}
                          data-testid="input-batch"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">In Time *</Label>
                      <Input
                        type="time"
                        value={multiPartEnabled ? (line.inTime || '') : (line.inTime || '08:00')}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            n[index] = { ...n[index], inTime: e.target.value };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        className={`${inputClass} w-40 font-mono`}
                        data-testid="input-part-in-time"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Out Time *</Label>
                      <Input
                        type="time"
                        value={multiPartEnabled ? (line.outTime || '') : (line.outTime || '20:00')}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            n[index] = { ...n[index], outTime: e.target.value };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        className={`${inputClass} w-40 font-mono`}
                        data-testid="input-part-out-time"
                      />
                    </div>
                  </div>

                  {/* Row 2: Cycle / Target / Produced */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Cycle Time (sec) *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={line.cycleTime}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            n[index] = { ...n[index], cycleTime: e.target.value };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        placeholder="0"
                        className={`${inputClass} w-40 font-mono`}
                        data-testid="input-cycle-time"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Target Qty *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={line.targetQty}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            n[index] = { ...n[index], targetQty: e.target.value };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        className={`${inputClass} w-40 font-mono`}
                        data-testid="input-target-qty"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Produced *</Label>
                      <Input
                        type="number"
                        min={0}
                        value={line.producedQty}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            n[index] = { ...n[index], producedQty: e.target.value };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        className={`${inputClass} w-40 font-mono bg-green-50 border-green-200`}
                        data-testid="input-produced-qty"
                      />
                    </div>
                    {/* Spacer to keep 4-column layout alignment */}
                    <div className="hidden lg:block" />
                  </div>

                  {/* Row 3: Rejection + Rework backlog */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
                    <div className="space-y-1.5 sm:col-span-2 lg:col-span-2">
                      <Label className="text-xs font-medium">
                        Rejection (qty + reason)
                      </Label>
                      <div className="space-y-2">
                        {(line.rejectionLines || []).map(function(row, ri) {
                          const qty = row?.qty ?? '';
                          const reasonId = row?.reasonId ?? '';
                          const options = rejectionReasons.map(function(rr) {
                            return {
                              value: rr.id,
                              label: (rr.code || '') + (rr.description ? ' - ' + rr.description : ''),
                            };
                          });
                          return (
                            <div key={ri} className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                value={qty}
                                onChange={(e) =>
                                  setPartLines(function(prev) {
                                    const n = [...prev];
                                    const lines = Array.isArray(n[index].rejectionLines)
                                      ? [...n[index].rejectionLines]
                                      : [];
                                    lines[ri] = {
                                      ...(lines[ri] || { qty: '', reasonId: '', reasonLabel: '' }),
                                      qty: e.target.value,
                                    };
                                    n[index] = { ...n[index], rejectionLines: lines };
                                    return n;
                                  })
                                }
                                disabled={isReadOnly}
                                className={`${inputClass} w-20 font-mono bg-red-50 border-red-200`}
                                placeholder="0"
                              />
                              <div className="w-40">
                                <SearchableDropdownBox
                                  options={options}
                                  value={reasonId}
                                  onChange={function(v) {
                                    setPartLines(function(prev) {
                                      const n = [...prev];
                                      const lines = Array.isArray(n[index].rejectionLines)
                                        ? [...n[index].rejectionLines]
                                        : [];
                                      const found = rejectionReasons.find(function(rr) { return rr.id === v; });
                                      const label = found
                                        ? (found.code || '') + (found.description ? ' - ' + found.description : '')
                                        : '';
                                      lines[ri] = {
                                        ...(lines[ri] || { qty: '', reasonId: '', reasonLabel: '', reasonCode: '' }),
                                        reasonId: v,
                                        reasonLabel: label,
                                        reasonCode: found?.code || '',
                                      };
                                      n[index] = { ...n[index], rejectionLines: lines };
                                      return n;
                                    });
                                  }}
                                  placeholder="Reason"
                                  disabled={isReadOnly}
                                  searchPlaceholder="Type to search reason..."
                                  emptyMessage="No reason found."
                                  triggerClassName="w-full"
                                />
                              </div>
                              {!isReadOnly && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={function() {
                                    setPartLines(function(prev) {
                                      const n = [...prev];
                                      const lines = Array.isArray(n[index].rejectionLines)
                                        ? [...n[index].rejectionLines]
                                        : [];
                                      lines.splice(ri, 1);
                                      n[index] = { ...n[index], rejectionLines: lines };
                                      return n;
                                    });
                                  }}
                                  className="text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                        {!isReadOnly && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={function() {
                              setPartLines(function(prev) {
                                const n = [...prev];
                                const lines = Array.isArray(n[index].rejectionLines)
                                  ? [...n[index].rejectionLines]
                                  : [];
                                lines.push({ qty: '', reasonId: '', reasonLabel: '' });
                                n[index] = { ...n[index], rejectionLines: lines };
                                return n;
                              });
                            }}
                          >
                            + Add rejection row
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Rework backlog</Label>
                      <Input
                        type="number"
                        readOnly
                        value={line.reworkBacklog ?? 0}
                        className={`${inputClass} font-mono bg-slate-50`}
                      />
                      <Label className="text-[10px] text-slate-500 mt-1">Reworked this run</Label>
                      <Input
                        type="number"
                        min={0}
                        value={line.reworkedThisRun}
                        onChange={(e) =>
                          setPartLines(function(prev) {
                            const n = [...prev];
                            let v = e.target.value;
                            if (v === '') {
                              n[index] = { ...n[index], reworkedThisRun: '' };
                              return n;
                            }
                            const num = parseInt(v, 10) || 0;
                            const max = line.reworkBacklog ?? 0;
                            if (num > max) {
                              toast.error('Reworked qty cannot exceed rework backlog');
                              return prev;
                            }
                            n[index] = { ...n[index], reworkedThisRun: String(num) };
                            return n;
                          })
                        }
                        disabled={isReadOnly}
                        className={`${inputClass} font-mono`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {multiPartEnabled
              ? 'Multi part: add In/Out time per part; target uses full shift duration. You can add more parts above.'
              : 'Single part: In/Out time and target use shift timings (working hours 10h/7h for 12h/8h shifts).'}
          </p>
        </CardContent>
      </Card>

      {/* Production Metrics Card */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Shift &amp; Downtime</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-medium">Downtime (hrs + mins + reason per row)</Label>
              <div className="space-y-2">
                {(downtimeLines || []).map(function(row, di) {
                  const options = downtimeReasons.map(function(dr) {
                    return { value: dr.id, label: toSingleLine((dr.code || '') + (dr.description ? ' - ' + dr.description : '')) };
                  });
                  return (
                    <div key={di} className="flex items-center gap-2 flex-nowrap">
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        value={row.hrs ?? ''}
                        onChange={(e) => {
                          setDowntimeLines(function(prev) {
                            const n = [...prev];
                            n[di] = { ...(n[di] || {}), hrs: e.target.value };
                            return n;
                          });
                        }}
                        disabled={isReadOnly}
                        placeholder="0"
                        className={`${inputClass} w-40 text-center font-mono`}
                        data-testid="input-downtime-hrs"
                      />
                      <span className="text-slate-500 text-xs">hrs</span>
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        value={row.mins ?? ''}
                        onChange={(e) => {
                          setDowntimeLines(function(prev) {
                            const n = [...prev];
                            n[di] = { ...(n[di] || {}), mins: e.target.value };
                            return n;
                          });
                        }}
                        disabled={isReadOnly}
                        placeholder="0"
                        className={`${inputClass} w-40 text-center font-mono`}
                        data-testid="input-downtime-mins"
                      />
                      <span className="text-slate-500 text-xs">mins</span>
                      <div className="flex-1 min-w-[140px]">
                        <SearchableDropdownBox
                          options={options}
                          value={row.reasonId ?? ''}
                          onChange={function(v) {
                            setDowntimeLines(function(prev) {
                              const n = [...prev];
                              const found = downtimeReasons.find(function(dr) { return dr.id === v; });
                              n[di] = {
                                ...(n[di] || {}),
                                reasonId: v,
                                reasonCode: found?.code || '',
                                reasonLabel: found ? toSingleLine((found.code || '') + (found.description ? ' - ' + found.description : '')) : '',
                              };
                              return n;
                            });
                          }}
                          placeholder="Reason"
                          disabled={isReadOnly}
                          searchPlaceholder="Type to search reason..."
                          emptyMessage="No reason found."
                          triggerClassName="w-full"
                          triggerTestId="downtime-reason-dropdown"
                        />
                      </div>
                      {!isReadOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={function() {
                            setDowntimeLines(function(prev) {
                              const n = prev.filter(function(_, i) { return i !== di; });
                              if (n.length === 0) n.push({ hrs: '', mins: '', reasonId: '', reasonCode: '', reasonLabel: '' });
                              return n;
                            });
                          }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {!isReadOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={function() {
                      setDowntimeLines(function(prev) {
                        return [...(prev || []), { hrs: '', mins: '', reasonId: '', reasonCode: '', reasonLabel: '' }];
                      });
                    }}
                  >
                    + Add downtime row
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-medium">Remarks *</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={isReadOnly} className="min-h-16 text-sm" placeholder="Any additional notes..." data-testid="input-remarks" required />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ProductionEntryPage;
